<script setup lang="ts">
import type { StandardMethod, StandardMethodMetaData } from '@/models/api-generated'
import { connectionColumns, visibleConnectionColumns } from './connectionColumns'
import {
	formatDate,
	formatLocale,
	getConnectionIcon,
	getDateFormat,
	getMethodStatusText,
	getPriorityText,
	getResponsibleIcon,
	getStatusIcon,
	hasPermission,
	priorityIconMap,
	truncate,
	viewing,
} from '@/services/utils'
import sliderStore from '@/stores/slider'
import methodEditor from '@/stores/method-editor'
import { ConditionallyImplemented, Implemented, NeedsAttention, PermBasicAudit, PermInternalAudit } from '@/models/enums-generated'
import MethodStatusSelect from '../widgets/MethodStatusSelect.vue'
import PrioritySelect from '../widgets/PrioritySelect.vue'
import Datepicker from '@vuepic/vue-datepicker'
import ResponsibleSelect from '../widgets/ResponsibleSelect.vue'
import IconButton from '../shared/IconButton.vue'
import NumericInput from '../widgets/NumericInput.vue'
import { computed, h, shallowRef, watch, nextTick } from 'vue'
import RisksTooltip from '../risk_management/RisksTooltip.vue'
import { storeToRefs } from 'pinia'
import { toAbsoluteUrl } from '@/services/utils'
import { useTheme } from '../user/useTheme'
import auditStore from '@/stores/audit'

const { theme } = useTheme()
const props = defineProps<{
	canEdit: boolean[]
}>()

const model = defineModel<StandardMethod>({ required: true })

const canAddBasic = computed(() => hasPermission(PermBasicAudit))
const canAddInternal = computed(() => hasPermission(PermInternalAudit))
const audit = auditStore()
const methodEditorStore = methodEditor()
const activeAudits = computed(() =>
	audit.activeBasic.filter((a) => !a.instance || (model.value.metaDatas ?? []).some((m) => m.instance?.id === a.instance?.id)),
)
const activeInternalAudits = computed(() =>
	audit.activeInternal.filter((a) => !a.instance || (model.value.metaDatas ?? []).some((m) => m.instance?.id === a.instance?.id)),
)
const activeForeignInternalAudits = computed(() =>
	audit.activeForeignInternal.filter((a) => !a.instance || (model.value.metaDatas ?? []).some((m) => m.instance?.id === a.instance?.id)),
)
const isActiveAudit = computed(
	() => activeAudits.value.length + activeInternalAudits.value.length + activeForeignInternalAudits.value.length > 0,
)

const emit = defineEmits<{
	(e: 'expand', metaIdx: number): void
}>()

const slider = sliderStore()
const { isEditMode } = storeToRefs(methodEditor())

function inputClass(reviewDate: string) {
	const today = new Date().toISOString()
	return { enabled: reviewDate === formatDate(today) }
}

const originalUpdatedAt = new Map<number, any>()
const setCurrentTime = (idx: number) => {
	if (!originalUpdatedAt.has(idx)) {
		originalUpdatedAt.set(idx, model.value.metaDatas[idx].updatedAt)
		model.value.metaDatas[idx].updatedAt = new Date().toISOString()
	} else {
		model.value.metaDatas[idx].updatedAt = originalUpdatedAt.get(idx)
		originalUpdatedAt.delete(idx)
	}
}

function goToRisks(names: string[]) {
	if (viewing.value === 0) return
	const params = new URLSearchParams()
	names.forEach((name) => {
		params.append('name', name)
	})
	window.location.href = `/app/risk-management?${params}`
}

const isEdit = shallowRef<boolean[]>([])

watch(isEditMode, () => (isEdit.value = props.canEdit.map((x) => isEditMode.value && x)), { immediate: true })

async function moveToCommentBox(metaId: number) {
	const metaIdx = model.value.metaDatas.findIndex((meta) => meta.id === metaId)
	if (metaIdx < 0) return
	emit('expand', metaIdx)
	await nextTick()
	await nextTick()

	const triggerEl = document.getElementById(`in-audit-icon${metaId}`) || document.getElementById(`add-one-to-audit-btn${metaId}`)
	const row = triggerEl?.closest('tr') as HTMLElement | null
	if (row) {
		row.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' })
	}
}

function shouldShowDueDate(meta: StandardMethodMetaData) {
	if (!meta.dueDate) return false
	if (meta.status === Implemented || meta.status === ConditionallyImplemented) return false
	return true
}
</script>

<template>
	<td v-if="visibleConnectionColumns.length">
		<div class="connection-column">
			<div
				v-for="(meta, i) in model.metaDatas"
				:key="meta.id"
				class="icon-container"
				:class="{ edit: isEdit[i] }"
				v-one-tooltip="getConnectionIcon(model.module.type, meta.asset?.name).title"
			>
				<i class="badge bg-primary">{{ getConnectionIcon(model.module.type, meta.asset?.name).badgeText }}</i>
				<small v-if="meta.asset?.name" class="ms-2">{{ truncate(meta.asset.name) }}</small>
			</div>
		</div>
	</td>

	<td v-if="connectionColumns.instance.isVisible">
		<div class="connection-column">
			<div
				v-for="(meta, i) in model.metaDatas"
				:key="meta.id"
				class="icon-container"
				:class="{ edit: isEdit[i] }"
				v-one-tooltip="meta.instance.name"
			>
				{{ truncate(meta.instance.name) }}
			</div>
		</div>
	</td>

	<td v-if="connectionColumns.responsible.isVisible">
		<div class="connection-column">
			<div v-for="(meta, i) in model.metaDatas" :key="meta.id" class="icon-container" :class="{ edit: isEdit[i] }">
				<div v-if="!isEdit[i]">
					<i v-if="meta.responsible" :class="getResponsibleIcon(meta.responsible)"></i>
					{{ meta.responsible ? meta.responsible.label : '-' }}
				</div>
				<div class="w-100" v-else>
					<ResponsibleSelect :id="'responsible-select' + meta.id" class="input-box mb-1" v-model="model.metaDatas[i].responsible" />
				</div>
			</div>
		</div>
	</td>

	<td v-if="connectionColumns.serviceProvider.isVisible">
		<div class="connection-column">
			<div v-for="(meta, i) in model.metaDatas" :key="meta.id" class="icon-container" :class="{ edit: isEdit[i] }">
				<div v-if="!isEdit[i]">
					<i v-if="meta.serviceProvider" class="bi-person-fill me-2 pie5"></i>
					{{ meta.serviceProvider ? meta.serviceProvider.label : '-' }}
				</div>
				<div class="w-100" v-else>
					<ResponsibleSelect :id="'service-provider-select' + meta.id" class="input-box mb-1" v-model="meta.serviceProvider" />
				</div>
			</div>
		</div>
	</td>

	<td v-if="connectionColumns.priority.isVisible">
		<div class="connection-column">
			<div
				v-for="(meta, i) in model.metaDatas"
				:key="meta.id"
				class="icon-container"
				:class="{ edit: isEdit[i] }"
				v-one-tooltip="getPriorityText(meta.priority)"
			>
				<div v-if="!isEdit[i]">
					<i v-if="priorityIconMap[meta.priority]" :class="priorityIconMap[meta.priority]"></i>
					<span v-else>-</span>
				</div>
				<div v-else>
					<PrioritySelect :id="'priority-select' + meta.id" v-model="model.metaDatas[i].priority" class="input-box mb-1" />
				</div>
			</div>
		</div>
	</td>

	<td v-if="connectionColumns.status.isVisible">
		<div class="connection-column">
			<div
				v-for="(meta, i) in model.metaDatas"
				:key="meta.id"
				class="icon-container"
				:class="{ edit: isEdit[i] }"
				v-one-tooltip="getMethodStatusText(meta.status)"
			>
				<div v-if="!isEdit[i]">
					<i :class="getStatusIcon(meta.status, model.isRenewed).iconClass"></i>
					<i v-if="meta.status === NeedsAttention && model.isRenewed" class="asterisk-icon bi bi-asterisk text-white"></i>
				</div>
				<div v-else>
					<MethodStatusSelect
						:id="'method-status-select' + meta.id"
						v-model="model.metaDatas[i].status"
						class="input-box mb-1"
						@focus="emit('expand', i)"
					/>
				</div>
			</div>
		</div>
	</td>

	<td v-if="connectionColumns.price.isVisible">
		<div class="connection-column">
			<div v-for="(meta, i) in model.metaDatas" :key="meta.id" class="icon-container" :class="{ edit: isEdit[i] }">
				<div v-if="!isEdit[i]">{{ meta.price || '-' }}</div>
				<div v-else>
					<NumericInput :id="'price-input' + meta.id" class="form-control input-box mb-1" v-model="model.metaDatas[i].price" />
				</div>
			</div>
		</div>
	</td>

	<td v-if="connectionColumns.auditCheck.isVisible">
		<div class="connection-column">
			<div v-for="meta in model.metaDatas" :key="meta.id" class="icon-container">
				<span v-if="meta.auditChecks && meta.auditChecks.total > 0" class="d-flex gap-1 align-items-center">
					<span v-if="meta.auditChecks.noKind > 0" v-one-tooltip="$t('audit.comment')" class="me-1">
						<i class="audit-icon bi bi-chat-right-text text-primary"></i>{{ meta.auditChecks.noKind }}
					</span>

					<span v-if="meta.auditChecks.ok > 0" v-one-tooltip="$t('audit.ok')" class="me-1">
						<i class="audit-icon bi bi-check-circle text-success"></i>{{ meta.auditChecks.ok }}
					</span>
					<span v-if="meta.auditChecks.remark > 0" v-one-tooltip="$t('audit.remark')" class="me-1">
						<i class="audit-icon bi bi-exclamation-circle text-warning"></i>{{ meta.auditChecks.remark }}
					</span>
					<span v-if="meta.auditChecks.defect > 0" v-one-tooltip="$t('audit.defect')" class="me-1">
						<i class="audit-icon bi bi-ban text-danger"></i>{{ meta.auditChecks.defect }}
					</span>
					<span
						v-if="meta.auditChecks.inAudit > 0 && meta.auditChecks.ok == 0 && meta.auditChecks.remark == 0 && meta.auditChecks.defect == 0"
						v-one-tooltip="$t('audit.inAudit')"
						class="me-1"
					>
						<IconButton
							v-if="isActiveAudit && (canAddBasic || canAddInternal)"
							:id="'in-audit-icon' + meta.id"
							class="audit-icon bi bi-clipboard-check text-info"
							v-one-tooltip="$t('audit.inAudit')"
							:size="isEditMode ? '' : 'fs-6'"
							@mouseup="moveToCommentBox(meta.id)"
						>
						</IconButton>
						<span v-else>
							<i class="audit-icon bi bi-clipboard-check text-info"></i>
						</span>
					</span>
				</span>
				<span v-else>
					<IconButton
						v-if="isActiveAudit && (canAddBasic || canAddInternal)"
						:id="'add-one-to-audit-btn' + meta.id"
						class="bi bi-clipboard-plus audit-icon"
						v-one-tooltip="$t('audit.addThisToAudit')"
						:size="isEditMode ? '' : 'fs-6'"
						@mouseup="methodEditorStore.addMethodToAudit(meta.id)"
					>
					</IconButton>
					<span v-else>-</span>
				</span>
			</div>
		</div>
	</td>

	<td v-if="connectionColumns.date.isVisible">
		<div class="connection-column">
			<div
				v-for="(meta, i) in model.metaDatas"
				:key="meta.id"
				:class="{
					'icon-container': true,
					edit: isEdit[i],
					'text-danger': new Date(meta.dueDate) < new Date() && shouldShowDueDate(meta),
				}"
			>
				<div v-if="!isEdit[i]">
					<template v-if="!shouldShowDueDate(meta)"> - </template>
					<template v-else>
						{{ formatDate(meta.dueDate) }}
					</template>
				</div>
				<div v-else>
					<Datepicker
						v-model="model.metaDatas[i].dueDate"
						:id="'date-select' + meta.id"
						:clearable="true"
						:teleport="true"
						:enable-time-picker="false"
						:format="getDateFormat()"
						:format-locale="formatLocale()"
						:select-text="$t('misc.select')"
						:cancel-text="$t('misc.cancel')"
						text-input
						class="input-box mb-1"
						:dark="theme === 'dark'"
					/>
				</div>
			</div>
		</div>
	</td>

	<td v-if="connectionColumns.comment.isVisible">
		<div class="connection-column">
			<div v-for="(meta, i) in model.metaDatas" :key="meta.id" class="icon-container" :class="{ edit: isEdit[i] }">
				<div v-if="!isEdit[i]">
					<span v-one-tooltip="meta.comment ? meta.comment : ''">
						{{ truncate(meta.comment) }}
					</span>
				</div>
				<div v-else>
					<input type="text" :id="'comment-input' + meta.id" maxlength="500" class="form-control input-box mb-1" v-model="meta.comment" />
				</div>
			</div>
		</div>
	</td>

	<td v-if="connectionColumns.lastReview.isVisible">
		<div class="connection-column">
			<div v-for="(meta, i) in model.metaDatas" :key="meta.id" class="icon-container" :class="{ edit: isEdit[i] }">
				<div v-if="!isEdit[i]">{{ meta.updatedAt ? formatDate(meta.updatedAt) : '-' }}</div>
				<div v-else>
					<button type="button" class="btn btn-outline-secondary mb-1" :class="inputClass(meta.updatedAt)" @click="setCurrentTime(i)">
						{{ meta.updatedAt ? formatDate(meta.updatedAt) : '-' }}
					</button>
				</div>
			</div>
		</div>
	</td>

	<td v-if="connectionColumns.changeLog.isVisible">
		<div class="connection-column">
			<div v-for="(meta, i) in model.metaDatas" :key="meta.id" class="icon-container" :class="{ edit: isEdit[i] }">
				<IconButton
					:id="'history-btn' + meta.id"
					class="bi bi-book"
					v-one-tooltip="$t('history.label')"
					:size="isEditMode ? '' : 'fs-6'"
					@mouseup="slider.openMethodHistory(meta.id, model.name, meta.asset.name)"
				>
				</IconButton>
			</div>
		</div>
	</td>

	<td v-if="connectionColumns.urlReference.isVisible">
		<div class="connection-column method-expand-icon">
			<template v-for="(meta, i) in model.metaDatas" :key="meta.id">
				<template v-if="meta.urlReferences">
					<template v-if="model.metaDatas.length === 1 || meta.urlReferences.length == 1">
						<a
							v-for="(ref, rIdx) in meta.urlReferences"
							:key="rIdx"
							:href="toAbsoluteUrl(ref.url.url)"
							target="_blank"
							class="icon-container"
							:class="{ edit: isEdit[i] }"
							v-one-tooltip="ref.url.url"
						>
							{{ truncate(ref.title || ref.url.url) }}
						</a>
					</template>
					<a v-else href="#" v-one-tooltip="meta.urlReferences.map((r) => r.url).join(', ')" @click.prevent="emit('expand', i)">
						{{ meta.urlReferences.length }} {{ $t('reference.label', meta.urlReferences.length) }}
					</a>
				</template>
				<template v-else>
					<span>-</span>
				</template>
			</template>
		</div>
	</td>

	<td v-if="connectionColumns.documentReference.isVisible">
		<div class="connection-column method-expand-icon">
			<template v-for="(meta, i) in model.metaDatas" :key="meta.id">
				<template v-if="meta.documentReferences">
					<template v-if="model.metaDatas.length === 1 || meta.documentReferences.length == 1">
						<span
							v-for="(ref, rIdx) in meta.documentReferences"
							:key="rIdx"
							class="icon-container"
							:class="{ edit: isEdit[i] }"
							v-one-tooltip="ref.document.name"
						>
							{{ truncate(ref.title || ref.document.name) }}
						</span>
					</template>
					<span v-else v-one-tooltip="meta.documentReferences.map((r) => r.document.name).join(', ')" @click.prevent="emit('expand', i)">
						{{ meta.documentReferences.length }} {{ $t('reference.label', meta.documentReferences.length) }}
					</span>
				</template>
				<template v-else>
					<span>-</span>
				</template>
			</template>
		</div>
	</td>
	<td v-if="connectionColumns.risks.isVisible && viewing > 0">
		<div class="connection-column">
			<div
				v-for="meta in model.metaDatas"
				:key="meta.id"
				class="icon-container risks-cell"
				v-one-tooltip="{
					content: () => h(RisksTooltip, { risks: meta.risks }),
				}"
				@click.stop="goToRisks(meta.risks.map((r) => r.name))"
			>
				{{ meta.risks ? meta.risks.length : 0 }}
			</div>
		</div>
	</td>

	<td v-if="visibleConnectionColumns.length" class="text-center">
		<span v-if="model.module.type === 'P'">-</span>
		<span v-else>{{ model.metaDatas.length }}</span>
	</td>
</template>

<style lang="sass" scoped>
.connection-column
  white-space: nowrap
.icon-container:not(.edit)
  height: 2rem
.icon-container.edit
  height: 3rem
.audit-icon
  margin-right: 0.125rem
</style>
